import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import RiskDisclosure from "./pages/RiskDisclosure";
import JoinMembership from "./pages/JoinMembership";
import Dashboard from "./pages/Dashboard";
import OptionsReadiness from "./pages/OptionsReadiness";
import Research from "./pages/Research";
import Scanner from "./pages/Scanner";
import Trending from "./pages/Trending";
import Power25 from './pages/Power25';
import Stocks from './pages/Stocks';
import Vibe from "./pages/Vibe";
import Watchlist from "./pages/Watchlist";
import VibeBot from "./pages/VibeBot";
import BotPilot from './pages/BotPilot';
import BotPositions from './pages/BotPositions';
import BotFeeReview from './pages/BotFeeReview';
import BotCostEvidence from './pages/BotCostEvidence';
import SocialTrading from "./pages/SocialTrading";
import PolygonWallet from "./pages/PolygonWallet";
import History from "./pages/History";
import LiquidityPool from "./pages/LiquidityPool";
import Admin from "./pages/Admin";
import Chat from "./pages/Chat";
import PendingApproval from "./pages/PendingApproval";
import PasswordReset from "./pages/PasswordReset";
import { PlatformProvider } from "./contexts/PlatformContext";
import { PlatformMaintenanceBoundary } from './components/PlatformMaintenanceBoundary';
import { Toaster } from "@/components/ui/sonner";
import { getLanguage } from './i18n/language';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;
axios.defaults.timeout = 10000; // 10 second timeout

// Configure axios for cross-domain cookies
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

// Add request interceptor for debugging
axios.interceptors.request.use(request => {
  if (request.url?.startsWith(`${API}/`)) request.headers['Accept-Language'] = getLanguage();
  console.log('📡 Axios Request:', request.method?.toUpperCase(), request.url);
  return request;
});

// Add response interceptor for debugging
axios.interceptors.response.use(
  response => {
    console.log('✅ Axios Response:', response.status, response.config.url);
    return response;
  },
  error => {
    if (!axios.isCancel(error) && !(error.response?.status === 401 && error.config?.url?.endsWith('/auth/me'))) {
      console.error('❌ Axios Error:', error.message, error.config?.url);
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    console.log('🔍 checkAuth called, loading:', loading);
    try {
      // Try to restore session token from localStorage
      const storedToken = localStorage.getItem('session_token');
      if (storedToken) {
        console.log('🔄 Restoring session token from localStorage');
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      console.log('✅ Auth check successful with user:', response.data.email);
    } catch (error) {
      console.log('❌ Auth check failed, clearing stored token');
      setUser(null);
      // Clear invalid token
      localStorage.removeItem('session_token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
      console.log('🏁 Auth check completed, loading set to false');
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const logout = async () => {
    console.log('🚪 Logging out and clearing session');
    
    try {
      // Call backend logout to invalidate session on server
      await axios.post(`${API}/auth/logout`);
      await axios.post(`${API}/payments/session/logout`);
      console.log('✅ Server-side logout successful');
    } catch (error) {
      console.warn('⚠️ Server-side logout failed (continuing with client cleanup):', error.message);
    }
    
    // Clear client-side session data
    setUser(null);
    localStorage.removeItem('session_token');
    delete axios.defaults.headers.common['Authorization'];
    
    console.log('🧹 Client-side session cleared');
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// Removed Google OAuth AuthHandler - now using cookie-based session authentication

function ProtectedRoute({ children }) {
  const { user, loading } = React.useContext(AuthContext);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/" />;
}

function AdminRoute({ children }) {
  const { user, loading } = React.useContext(AuthContext);
  if (loading) return <div role="status" data-testid="admin-role-loading">Checking access…</div>;
  if (!user) return <Navigate to="/" replace />;
  return user.is_admin ? children : <Navigate to="/dashboard" replace />;
}

// AppContent component that uses platform context
function AppContent() {
  
  const [announcements, setAnnouncements] = useState([
    {
      id: 1,
      title: "System Maintenance Scheduled",
      message: "We will be performing scheduled maintenance on December 15th from 2:00 AM to 4:00 AM UTC. Trading will be temporarily unavailable during this time.",
      type: "warning",
      active: false
    },
    {
      id: 2,
      title: "New AI Trading Bot Available!",
      message: "Introducing our latest AI trading bot with advanced market analysis. Check out the Social Trading section to start copying trades.",
      type: "info",
      active: false
    }
  ]);

  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <PlatformMaintenanceBoundary>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/risk-disclosure" element={<RiskDisclosure />} />
            <Route path="/join" element={<JoinMembership />} />
            <Route path="/payment/success" element={<JoinMembership />} />
            <Route path="/payment/cancel" element={<JoinMembership />} />
            <Route path="/pending" element={<PendingApproval />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/polygon-wallet" element={<ProtectedRoute><PolygonWallet /></ProtectedRoute>} />
            <Route path="/trade" element={<ProtectedRoute><PolygonWallet tradePage /></ProtectedRoute>} />
            <Route path="/options" element={<ProtectedRoute><OptionsReadiness /></ProtectedRoute>} />
            <Route path="/research" element={<ProtectedRoute><Research /></ProtectedRoute>} />
            <Route path="/scanner" element={<ProtectedRoute><Scanner /></ProtectedRoute>} />
            <Route path="/trending" element={<ProtectedRoute><Trending /></ProtectedRoute>} />
            <Route path="/stocks" element={<ProtectedRoute><Stocks /></ProtectedRoute>} />
            <Route path="/stocks/scanner" element={<ProtectedRoute><Stocks scanner /></ProtectedRoute>} />
            <Route path="/power25" element={<ProtectedRoute><Power25 /></ProtectedRoute>} />
            <Route path="/vibe" element={<ProtectedRoute><Vibe /></ProtectedRoute>} />
            <Route path="/watchlist" element={<ProtectedRoute><Watchlist /></ProtectedRoute>} />
            <Route path="/social-trading/vibe-bot" element={<ProtectedRoute><VibeBot /></ProtectedRoute>} />
            <Route path="/bot-pilot" element={<ProtectedRoute><BotPilot /></ProtectedRoute>} />
            <Route path="/bot-positions" element={<ProtectedRoute><BotPositions /></ProtectedRoute>} />
            <Route path="/bot-fee-review" element={<ProtectedRoute><BotFeeReview /></ProtectedRoute>} />
            <Route path="/bot-cost-evidence" element={<ProtectedRoute><BotCostEvidence /></ProtectedRoute>} />
            <Route path="/pool" element={<ProtectedRoute><LiquidityPool /></ProtectedRoute>} />
            <Route path="/social-trading" element={<ProtectedRoute><SocialTrading /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          </Routes>
          </PlatformMaintenanceBoundary>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function App() {
  return (
    <PlatformProvider>
      <AppContent />
    </PlatformProvider>
  );
}

export default App;